/**
 * evaluateRowFormulas
 *
 * Evaluates an ordered formula sequence for a single row. Formulas run in
 * array order, and later formulas read earlier results by typeId through the
 * results map — that ordered dependency is the feature. A variable naming an
 * engine-owned typeId (resolveArFormulaOwnedTypeIds: any target, or a dotted
 * target's root column) NEVER falls back to the raw cell: uncomputed, it
 * resolves to 0, the engine's standard missing-operand coercion. The raw-cell
 * fallback let the engine read its own persisted output, so a self- or
 * forward-reference oscillated per pass and the reconcile trigger rewrote the
 * row forever (SYD-2292). Do not "optimize" the fallback back in.
 */

import evaluateFormula from './evaluateFormula';
import { resolveArFormulaOwnedTypeIds } from './resolveArFormulaOwnedTypeIds';
import type { ArCellValue, ArFormulaEntry, TypeId } from '../System.Artifacts.types';

type CellGetter = (typeId: TypeId) => ArCellValue;

function evaluateRowFormulas(
  formulaSequence: ArFormulaEntry[],
  getCellValue: CellGetter
): Map<TypeId, number | null> {
  const owned = resolveArFormulaOwnedTypeIds(formulaSequence);
  const results = new Map<TypeId, number | null>();

  const getValue = (typeId: TypeId): ArCellValue => {
    if (owned.has(typeId as string)) {
      return results.get(typeId) ?? 0;
    }
    return getCellValue(typeId);
  };

  for (const { typeId, formula } of formulaSequence) {
    const result = evaluateFormula(formula, getValue);
    results.set(typeId, result);
  }

  return results;
}

export default evaluateRowFormulas;
